import React, { useRef, useState } from 'react';

export interface FilterMultiSelectOption {
  value: string;
  label: string;
}

export interface FilterMultiSelectProps {
  label: string;
  values: string[];
  options: FilterMultiSelectOption[];
  onChange: (values: string[]) => void;
}

/**
 * FilterMultiSelect — multi-value dropdown with checkboxes.
 * Displays selected values inline when closed.
 */
export default function FilterMultiSelect({
  label,
  values,
  options,
  onChange,
}: FilterMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  function handleToggle(value: string) {
    const newValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(newValues);
  }

  function handleClickOutside(e: MouseEvent) {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setIsOpen(false);
    }
  }

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const selectedLabels = options
    .filter((opt) => values.includes(opt.value))
    .map((opt) => opt.label);

  return (
    <div ref={containerRef} className="min-h-[44px] flex flex-col gap-1 relative">
      <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Filter by ${label}`}
        aria-expanded={isOpen}
        className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm text-left focus:outline-none focus:ring-2 focus:ring-accent min-h-[40px] flex items-center justify-between"
      >
        <span className="flex-1 overflow-hidden text-ellipsis">
          {selectedLabels.length === 0 ? (
            <span className="text-fg-muted">Select…</span>
          ) : (
            <span>
              {selectedLabels.slice(0, 2).join(', ')}
              {selectedLabels.length > 2
                ? ` +${selectedLabels.length - 2}`
                : ''}
            </span>
          )}
        </span>
        <span className="ml-2 text-fg-muted">▼</span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-elevated border border-border-base rounded z-10 shadow-lg">
          <div className="max-h-48 overflow-y-auto">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-2 hover:bg-bg-raised/50 cursor-pointer text-sm text-fg-primary"
              >
                <input
                  type="checkbox"
                  checked={values.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  className="accent-emerald-500"
                  aria-label={opt.label}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
